import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, AuthContextType, SessionInfo } from '../types';
import api, { tokenStore, TOKEN_KEY } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error: any) {
      console.error('Failed to fetch user:', error);
      setUser(null);
      // Only remove token if it's an explicit authentication error
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        tokenStore.clear();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get('/auth/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, []);

  useEffect(() => {
    const token = tokenStore.get();
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }

    const handleUnauthorized = () => {
      tokenStore.clear();
      setUser(null);
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY) {
        if (!e.newValue) {
          // Token was removed (e.g., logout in another tab)
          setUser(null);
        } else if (e.newValue !== e.oldValue) {
          // Token changed (e.g., login in another tab)
          fetchUser();
        }
      }
    };

    // Real-time role revocation/promotion handler (Socket.IO + 403 fallback)
    const handleRoleUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ userId?: string; isPromoted?: boolean; isAdmin?: boolean; reason?: string }>;
      const detail = customEvent.detail;
      if (!detail) return;

      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (typeof detail.isPromoted === 'boolean') {
          updated.isPromoted = detail.isPromoted;
        }
        if (typeof detail.isAdmin === 'boolean') {
          updated.isAdmin = detail.isAdmin;
        }
        return updated;
      });

      // Synchronize with authoritative /auth/me
      fetchUser();

      if (detail.isPromoted === false) {
        toast.error(detail.reason || 'Your seller membership has been revoked by an administrator.');
        if (window.location.pathname.startsWith('/seller')) {
          window.location.href = '/promotion';
        }
      } else if (detail.isAdmin === false) {
        toast.error(detail.reason || 'Your administrator privileges have been removed.');
        if (window.location.pathname.startsWith('/admin')) {
          window.location.href = '/';
        }
      } else if (detail.isPromoted === true) {
        toast.success(detail.reason || 'Your seller account has been activated!');
      }
    };

    // Tab focus revalidation: if user returns to tab after more than 10s, check /auth/me
    let lastFocusCheck = Date.now();
    const handleTabRevisit = () => {
      if (Date.now() - lastFocusCheck > 10000 && tokenStore.get()) {
        lastFocusCheck = Date.now();
        fetchUser();
      }
    };

    window.addEventListener('auth-unauthorized', handleUnauthorized);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-role-updated', handleRoleUpdated);
    window.addEventListener('focus', handleTabRevisit);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') handleTabRevisit();
    });

    // Session expiry watchdog: when the access token can no longer be
    // refreshed, clear local state so the user is redirected to sign-in.
    const watchdog = window.setInterval(() => {
      if (!tokenStore.getRefreshToken() && tokenStore.get()) {
        // Refresh token already gone (e.g. revoked server-side) — check if
        // our access token is still accepted; the interceptor handles the rest.
        fetchUser();
      }
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('auth-unauthorized', handleUnauthorized);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-role-updated', handleRoleUpdated);
      window.removeEventListener('focus', handleTabRevisit);
      window.clearInterval(watchdog);
    };
  }, [fetchUser]);

  const signIn = async (email: string, password: string, remember: boolean = true) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData, refreshToken, sessionId } = response.data;
    tokenStore.set(token, remember);
    if (refreshToken) tokenStore.setRefreshToken(refreshToken, remember);
    if (sessionId) tokenStore.setSessionId(sessionId, remember);
    setUser(userData);
  };

  const signUp = async (email: string, password: string, name: string, phone: string, gender: string, referralCode?: string, sellerCode?: string) => {
    const response = await api.post('/auth/register', { email, password, name, phone, gender, referralCode, sellerCode });
    // If email verification is required, no token is issued — the UI shows a
    // "verify your email" screen and the user must confirm before signing in.
    if (response.data.requiresEmailVerification) {
      return { requiresEmailVerification: true, email };
    }
    const { token, user: userData, refreshToken, sessionId } = response.data;
    tokenStore.set(token, true);
    if (refreshToken) tokenStore.setRefreshToken(refreshToken, true);
    if (sessionId) tokenStore.setSessionId(sessionId, true);
    setUser(userData);
    return { requiresEmailVerification: false };
  };

  const signOut = async () => {
    try {
      await api.post('/auth/logout').catch(() => {});
    } catch (e) {
      // Best-effort server-side logout (session revoke). Never block sign-out.
    }
    tokenStore.clear();
    setSessions([]);
    setUser(null);
  };

  const signInWithGoogle = async (tokenOrPayload?: string | { accessToken?: string; idToken?: string }) => {
    if (!tokenOrPayload) {
      throw new Error('Google token is required.');
    }
    const payload = typeof tokenOrPayload === 'string'
      ? { accessToken: tokenOrPayload }
      : tokenOrPayload;
    if (!payload.accessToken && !payload.idToken) {
      throw new Error('Google access token or ID token is required.');
    }
    const response = await api.post('/auth/google', payload);
    const { token, user: userData, refreshToken, sessionId } = response.data;
    tokenStore.set(token, true);
    if (refreshToken) tokenStore.setRefreshToken(refreshToken, true);
    if (sessionId) tokenStore.setSessionId(sessionId, true);
    setUser(userData);
  };

  const resendVerificationEmail = async (email: string) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  };

  const verifyEmailOtp = async (email: string, otp: string) => {
    const response = await api.post('/auth/verify-email', { email, otp });
    const { token, user: userData, refreshToken, sessionId } = response.data;
    if (token) {
      tokenStore.set(token, true);
      if (refreshToken) tokenStore.setRefreshToken(refreshToken, true);
      if (sessionId) tokenStore.setSessionId(sessionId, true);
      setUser(userData);
    }
    return response.data;
  };

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const revokeSession = async (sessionId: string) => {
    await api.delete(`/auth/sessions/${sessionId}`);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  };

  const logoutAll = async () => {
    try {
      await api.post('/auth/logout-all').catch(() => {});
    } catch (e) {
      // Best-effort; local state must still be cleared.
    }
    tokenStore.clear();
    setSessions([]);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, signInWithGoogle, fetchUser, updateUser, resendVerificationEmail, verifyEmailOtp, sessions, fetchSessions, revokeSession, logoutAll }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
